import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import {
  categoryOptions,
  flowerColorOptions,
  leafColorOptions,
  soilTypeOptions,
  sunExposureOptions,
  formOptions,
  growthRateOptions,
  habitOptions,
  leafTypeOptions,
  floweringSeasonOptions,
  fruitsOptions,
  fruitingSeasonOptions,
  featuresOptions,
  gardenTypesOptions,
  soilMoistureOptions,
  soilPhOptions,
  usageOptions,
  hardinessZoneOptions
} from './data/plantOptions'
import { PlantNursery } from '../../types/plant'
import { Nursery } from '../../types/nursery'
import AppNavBar from '../Header/AppNavBar'

// Interfejs dla danych rośliny w formularzu
interface PlantFormData {
  name: string
  latinName: string
  image: string
  images: string[]
  category: string
  height: number
  spread: number
  flowerColor: string[]
  leafColor: string[]
  soilType: string[]
  sunExposure: string[]
  hardiness: string[]
  description: string
  properties: string[]
  nurseries: PlantNursery[]
  form: string[]
  growthRate: string
  habit: string[]
  leafType: string
  floweringSeason: string[]
  fruits: string[]
  fruitingSeason: string[]
  soilMoisture: string[]
  soilPh: string[]
  features: string[]
  gardenTypes: string[]
  usage: string[]
}

// Początkowe wartości formularza
const initialFormData: PlantFormData = {
  name: '',
  latinName: '',
  image: '',
  images: [],
  category: '',
  height: 0,
  spread: 0,
  flowerColor: [],
  leafColor: [],
  soilType: [],
  sunExposure: [],
  hardiness: [],
  description: '',
  properties: [],
  nurseries: [],
  form: [],
  growthRate: '',
  habit: [],
  leafType: '',
  floweringSeason: [],
  fruits: [],
  fruitingSeason: [],
  soilMoisture: [],
  soilPh: [],
  features: [],
  gardenTypes: [],
  usage: []
}

const PlantForm: React.FC = () => {
  const [formData, setFormData] = useState<PlantFormData>(initialFormData)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isEdit, setIsEdit] = useState<boolean>(false)

  // Dodaj stan dla listy dostępnych szkółek
  const [availableNurseries, setAvailableNurseries] = useState<Nursery[]>([])
  // Zmień stan dla szkółek przypisanych do rośliny
  const [selectedNurseries, setSelectedNurseries] = useState<PlantNursery[]>([
    { nurseryId: 0, availability: '', price: '' }
  ])

  // Dodaj stany do obsługi przesyłania zdjęć
  const [mainImageUploading, setMainImageUploading] = useState(false)
  const [additionalImagesUploading, setAdditionalImagesUploading] = useState(false)
  const [mainImagePreview, setMainImagePreview] = useState('')
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([])

  // Dodanie stanu ładowania dla szkółek
  const [nurseryLoading, setNurseryLoading] = useState<boolean>(false)

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const requests = useRequests()

  useEffect(() => {
    if (id) {
      setIsEdit(true)
      loadPlant(parseInt(id))
    }
  }, [id])

  // Modyfikacja useEffect do pobierania szkółek
  useEffect(() => {
    const fetchNurseries = async () => {
      setNurseryLoading(true)
      try {
        const response = await requests.getNurseries()

        // Lepsze sprawdzanie różnych formatów odpowiedzi
        if (Array.isArray(response.data)) {
          setAvailableNurseries(response.data)
        } else if (response.data && typeof response.data === 'object') {
          if (Array.isArray(response.data.nurseries)) {
            setAvailableNurseries(response.data.nurseries)
          } else if (Array.isArray(response.data.data)) {
            setAvailableNurseries(response.data.data)
          } else {
            // Jeśli odpowiedź ma inne pola, spróbuj znaleźć tablicę
            const potentialArrays = Object.entries(response.data)
              .filter(([_, value]) => Array.isArray(value))

            if (potentialArrays.length > 0) {
              // Dodajemy sprawdzenie typów i rzutowanie
              const potentialNurseries = potentialArrays[0][1]
              if (Array.isArray(potentialNurseries) &&
                  potentialNurseries.every(item =>
                    typeof item === 'object' &&
                    item !== null &&
                    'id' in item &&
                    'name' in item &&
                    'location' in item
                  )) {
                setAvailableNurseries(potentialNurseries as Nursery[])
              } else {
                console.error('Znaleziona tablica nie zawiera obiektów typu Nursery:', potentialNurseries)
                setAvailableNurseries([])
                setError('Otrzymano nieprawidłowy format danych szkółek.')
              }
            } else {
              console.error('Nie znaleziono tablicy szkółek w odpowiedzi:', response.data)
              setAvailableNurseries([])
              setError('Otrzymano nieprawidłowy format danych szkółek - nie znaleziono tablicy.')
            }
          }
        } else {
          console.error('Nieprawidłowy format danych szkółek:', response.data)
          setAvailableNurseries([])
          setError('Otrzymano nieprawidłowy format danych szkółek.')
        }
      } catch (err) {
        console.error('Błąd podczas pobierania listy szkółek:', err)
        setError('Nie udało się pobrać listy szkółek.')
        setAvailableNurseries([])
      } finally {
        setNurseryLoading(false)
      }
    }

    fetchNurseries()
  }, [requests])

  // Dodanie obsługi presigned URL do podglądu głównego zdjęcia
  useEffect(() => {
    const getImagePreview = async () => {
      if (formData.image) {
        // Jeśli zdjęcie ma już adres URL lub jest to nowo przesłane zdjęcie
        if (formData.image.startsWith('http')) {
          setMainImagePreview(formData.image)
        } else {
          try {
            // Pobierz presigned URL dla klucza pliku
            const response = await requests.getFileUrl(formData.image)
            setMainImagePreview(response.data.url)
          } catch (error) {
            console.error('Błąd podczas pobierania podglądu głównego zdjęcia', error)
          }
        }
      }
    }

    getImagePreview()
  }, [formData.image, requests])

  // Dodanie obsługi presigned URL dla dodatkowych zdjęć
  useEffect(() => {
    const getAdditionalImagePreviews = async () => {
      if (formData.images && formData.images.length > 0) {
        const previews = []

        for (const imageKey of formData.images) {
          if (!imageKey) continue // Pomijamy puste klucze

          if (imageKey.startsWith('http')) {
            previews.push(imageKey)
          } else {
            try {
              const response = await requests.getFileUrl(imageKey)
              previews.push(response.data.url)
            } catch (error) {
              console.error('Błąd podczas pobierania podglądu dodatkowego zdjęcia', error)
              previews.push('') // Dodajemy pustą wartość jako placeholder
            }
          }
        }

        if (previews.some(p => p)) { // Sprawdź, czy są jakieś niepuste podglądy
          setAdditionalImagePreviews(previews.filter((url: string | null) => url))
        }
      }
    }

    getAdditionalImagePreviews()
  }, [formData.images, requests])

  // Zmodyfikowana metoda loadPlant
  const loadPlant = async (plantId: number) => {
    setLoading(true)
    try {
      const response = await requests.getPlant(plantId)
      const plantData = response.data

      // Pobierz presigned URLs dla zdjęć
      try {
        const imageUrlsResponse = await requests.getPlantPresignedUrls(plantId)

        // Ustaw od razu podglądy zdjęć
        if (imageUrlsResponse.data.imagePresignedUrl) {
          setMainImagePreview(imageUrlsResponse.data.imagePresignedUrl)
        }

        if (imageUrlsResponse.data.imagesPresignedUrls && imageUrlsResponse.data.imagesPresignedUrls.length > 0) {
          setAdditionalImagePreviews(imageUrlsResponse.data.imagesPresignedUrls.filter((url: string | null) => url))
        }
      } catch (error) {
        console.error('Błąd podczas pobierania URL zdjęć', error)
      }

      // Przygotuj dane szkółek
      const nurseries = plantData.nurseries
        ? plantData.nurseries.map((n: any) => ({
          nurseryId: n.id,
          availability: n.availability || '',
          price: n.price || ''
        }))
        : []

      // Upewniamy się, że pliki są poprawnie ustawione
      setFormData(prevData => ({
        ...prevData,
        ...plantData,
        nurseries
      }))

      // Ustawienie szkółek przypisanych do rośliny
      if (nurseries.length > 0) {
        setSelectedNurseries(nurseries)
      }
    } catch (error) {
      console.error('Błąd podczas ładowania rośliny', error)
      setError('Nie udało się załadować danych rośliny')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }))
  }

  const handleMultiSelectChange = (name: keyof PlantFormData, value: string) => {
    setFormData(prev => {
      const currentValues = prev[name] as string[]
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value]
      return { ...prev, [name]: newValues }
    })
  }

  // Poprawiona funkcja obsługi zmiany szkółki
  const handleNurseryChange = (index: number, field: string, value: string) => {
    const updatedNurseries = [...selectedNurseries]

    if (field === 'nurseryId') {
      // Konwersja wartości string na number dla nurseryId
      updatedNurseries[index] = {
        ...updatedNurseries[index],
        [field]: parseInt(value) || 0 // Używamy parseInt z sprawdzeniem pustej wartości
      }
    } else {
      // Dla innych pól (availability, price) zachowujemy string
      updatedNurseries[index] = {
        ...updatedNurseries[index],
        [field]: value
      }
    }

    setSelectedNurseries(updatedNurseries)

    // Aktualizuj też główny stan formularza
    setFormData(prev => ({
      ...prev,
      nurseries: updatedNurseries
    }))
  }

  const addNursery = () => {
    setSelectedNurseries([...selectedNurseries, { nurseryId: 0, availability: '', price: '' }])
  }

  const removeNursery = (index: number) => {
    const updatedNurseries = [...selectedNurseries]
    updatedNurseries.splice(index, 1)
    setSelectedNurseries(updatedNurseries)
  }

  // Dodaj funkcję do obsługi przesyłania głównego zdjęcia
  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files == null) || e.target.files.length === 0) return

    try {
      setMainImageUploading(true)
      setError(null)

      const file = e.target.files[0]

      // Utwórz URL podglądu
      const previewUrl = URL.createObjectURL(file)
      setMainImagePreview(previewUrl)

      // Prześlij plik do serwera
      const response = await requests.uploadFile(file, 'plant')

      // Aktualizuj stan formularza
      setFormData(prev => ({
        ...prev,
        image: response.data.fileKey
      }))

      setMainImageUploading(false)
    } catch (err) {
      console.error('Błąd podczas przesyłania głównego zdjęcia:', err)
      setError('Nie udało się przesłać głównego zdjęcia.')
      setMainImageUploading(false)
    }
  }

  // Dodaj funkcję do obsługi przesyłania dodatkowych zdjęć
  const handleAdditionalImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files == null) || e.target.files.length === 0) return

    try {
      setAdditionalImagesUploading(true)
      setError(null)

      const files = Array.from(e.target.files)
      const uploadPromises = []
      const newImageKeys = [...formData.images]
      const newPreviews = [...additionalImagePreviews]

      for (const file of files) {
        // Utwórz URL podglądu
        const previewUrl = URL.createObjectURL(file)
        newPreviews.push(previewUrl)

        // Prześlij plik i zapisz promise
        uploadPromises.push(
          requests.uploadFile(file, 'plant')
            .then(response => {
              newImageKeys.push(response.data.fileKey)
            })
        )
      }

      // Zaktualizuj podglądy od razu
      setAdditionalImagePreviews(newPreviews)

      // Poczekaj na zakończenie wszystkich przesyłań
      await Promise.all(uploadPromises)

      // Zaktualizuj formData po zakończeniu wszystkich przesyłań
      setFormData(prev => ({
        ...prev,
        images: newImageKeys
      }))

      setAdditionalImagesUploading(false)
    } catch (err) {
      console.error('Błąd podczas przesyłania dodatkowych zdjęć:', err)
      setError('Nie udało się przesłać dodatkowych zdjęć.')
      setAdditionalImagesUploading(false)
    }
  }

  // Poprawiona funkcja usuwania dodatkowego obrazu
  const handleRemoveAdditionalImage = (index: number) => {
    // Aktualizacja stanu formData - usunięcie obrazu z tablicy images
    setFormData(prevData => {
      const updatedImages = [...prevData.images]
      updatedImages.splice(index, 1)
      return {
        ...prevData,
        images: updatedImages
      }
    })

    // Aktualizacja stanu podglądów obrazów
    setAdditionalImagePreviews(prevPreviews => {
      const updatedPreviews = [...prevPreviews]
      updatedPreviews.splice(index, 1)
      return updatedPreviews
    })
  }

  // Upewnijmy się, że przed wysłaniem formularza dane szkółek są poprawnie ustawione
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      if (isEdit) {
        // Aktualizacja istniejącej rośliny
        await requests.updatePlant(parseInt(id!), formData)

        // Wyświetl komunikat o sukcesie
        alert('Roślina została zaktualizowana!')

        // Przekieruj do szczegółów tej samej rośliny (mamy już ID)
        navigate(`/plants/${id}`)
      } else {
        // Dodawanie nowej rośliny
        const response = await requests.createPlant(formData)

        // Wyświetl komunikat o sukcesie
        alert('Roślina została dodana!')

        // Przekieruj do szczegółów nowo utworzonej rośliny (pobierz ID z odpowiedzi)
        const newPlantId = response.data.id
        navigate(`/plants/${newPlantId}`)
      }
    } catch (err) {
      console.error('Błąd podczas zapisywania rośliny:', err)
      setError('Wystąpił błąd podczas zapisywania. Sprawdź formularz i spróbuj ponownie.')
    } finally {
      setLoading(false)
    }
  }

  if (loading && isEdit) {
    return (
      <div className='text-center pt-100 pb-100'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        rightIcons={[]}
      />

      <section className='plant-form pt-60 pb-60'>
        <div className='container'>
          <div className='row'>
            <div className='col-lg-12'>
              {error && (
                <div className='alert alert-danger mb-30' role='alert'>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className='plant-form'>
                {/* Sekcja 1: Podstawowe informacje */}
                <div className='card mb-4 shadow-sm'>
                  <div className='card-header bg-light py-3'>
                    <h4 className='mb-0 font-weight-bold text-primary'>Podstawowe informacje</h4>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='form-group mb-3'>
                          <label htmlFor='name' className='font-weight-bold'>Nazwa rośliny <span className='text-danger'>*</span></label>
                          <input
                            type='text'
                            className='form-control'
                            id='name'
                            name='name'
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className='form-group mb-3'>
                          <label htmlFor='latinName' className='font-weight-bold'>Nazwa łacińska</label>
                          <input
                            type='text'
                            className='form-control'
                            id='latinName'
                            name='latinName'
                            value={formData.latinName}
                            onChange={handleChange}
                          />
                        </div>

                        <div className='form-group'>
                          <label htmlFor='category'>Grupa użytkowa</label>
                          <select
                            className='form-control'
                            id='category'
                            name='category'
                            value={formData.category}
                            onChange={handleChange}
                          >
                            <option value=''>Wybierz grupę użytkową</option>
                            {categoryOptions.map(option => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className='form-group'>
                          <label>Strefy mrozoodporności</label>
                          <div className='checkbox-group row'>
                            {hardinessZoneOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`hardiness-${option.id}`}
                                  checked={formData.hardiness.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('hardiness', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`hardiness-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='form-group mb-3'>
                          <label htmlFor='image' className='font-weight-bold'>Główne zdjęcie rośliny</label>

                          <div className='custom-file-upload mb-2'>
                            <input
                              type='file'
                              className='custom-file-input'
                              id='mainImageUpload'
                              accept='image/*'
                              onChange={handleMainImageUpload}
                              disabled={mainImageUploading}
                            />
                            <label className='custom-file-label' htmlFor='mainImageUpload'>
                              {mainImageUploading ? 'Przesyłanie...' : 'Wybierz plik'}
                            </label>
                          </div>

                          {(mainImagePreview || formData.image) && (
                            <div className='image-preview mb-2'>
                              <img
                                src={mainImagePreview || (formData.image.includes('/') ? formData.image : '')}
                                alt='Podgląd głównego zdjęcia'
                                className='img-thumbnail'
                                style={{ maxHeight: '200px' }}
                              />
                            </div>
                          )}

                          <input
                            type='text'
                            className='form-control'
                            id='image'
                            name='image'
                            value={formData.image}
                            onChange={handleChange}
                            placeholder='Lub podaj adres URL zdjęcia'
                          />
                        </div>

                        <div className='form-group mb-3'>
                          <label className='font-weight-bold'>Dodatkowe zdjęcia rośliny</label>
                          <div className='custom-file mb-3'>
                            <input
                              type='file'
                              className='custom-file-input'
                              id='additionalImages'
                              accept='image/*'
                              multiple
                              onChange={handleAdditionalImagesUpload}
                              disabled={additionalImagesUploading}
                            />
                            <label className='custom-file-label' htmlFor='additionalImages'>
                              {additionalImagesUploading ? 'Przesyłanie...' : 'Wybierz zdjęcia'}
                            </label>
                          </div>

                          {additionalImagePreviews.length > 0 && (
                            <div className='additional-images-preview mt-3'>
                              <div className='row'>
                                {additionalImagePreviews.map((preview, index) => (
                                  preview && (
                                    <div key={index} className='col-md-4 mb-3'>
                                      <div className='card h-100'>
                                        <img
                                          src={preview}
                                          alt={`Dodatkowe zdjęcie ${index + 1}`}
                                          className='card-img-top'
                                          style={{ height: '100px', objectFit: 'cover' }}
                                        />
                                        <div className='card-body p-2 text-center'>
                                          <button
                                            type='button'
                                            className={`
                                                tw-w-full tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                                tw-text-white
                                                tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                              `}
                                            onClick={() => handleRemoveAdditionalImage(index)}
                                          >
                                            <i className='fas fa-trash' /> Usuń
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sekcja 2: Wymiary i charakterystyka */}
                <div className='card mb-4 shadow-sm'>
                  <div className='card-header bg-light py-3'>
                    <h4 className='mb-0 font-weight-bold text-primary'>Wymiary i charakterystyka</h4>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='form-group mb-3'>
                          <label htmlFor='height' className='font-weight-bold'>Docelowa wysokość (m) <span className='text-danger'>*</span></label>
                          <input
                            type='number'
                            step='0.1'
                            className='form-control'
                            id='height'
                            name='height'
                            value={formData.height}
                            onChange={handleNumericChange}
                            required
                          />
                        </div>

                        <div className='form-group mb-3'>
                          <label htmlFor='spread' className='font-weight-bold'>Docelowa szerokość (m) <span className='text-danger'>*</span></label>
                          <input
                            type='number'
                            step='0.1'
                            className='form-control'
                            id='spread'
                            name='spread'
                            value={formData.spread}
                            onChange={handleNumericChange}
                            required
                          />
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='form-group'>
                          <label>Kolor kwiatów</label>
                          <div className='checkbox-group row'>
                            {flowerColorOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`flowerColor-${option.id}`}
                                  checked={formData.flowerColor.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('flowerColor', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`flowerColor-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className='form-group'>
                          <label>Kolor liści</label>
                          <div className='checkbox-group row'>
                            {leafColorOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`leafColor-${option.id}`}
                                  checked={formData.leafColor.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('leafColor', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`leafColor-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sekcja 3: Wymagania i opisy */}
                <div className='card mb-4 shadow-sm'>
                  <div className='card-header bg-light py-3'>
                    <h4 className='mb-0 font-weight-bold text-primary'>Wymagania i opisy</h4>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <h5 className='border-bottom pb-2 mb-3'>Wymagania</h5>
                        <div className='form-group mb-3'>
                          <label className='font-weight-bold'>Typ gleby</label>
                          <div className='checkbox-group row'>
                            {soilTypeOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`soilType-${option.id}`}
                                  checked={formData.soilType.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('soilType', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`soilType-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className='form-group'>
                          <label>Nasłonecznienie</label>
                          <div className='checkbox-group row'>
                            {sunExposureOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`sunExposure-${option.id}`}
                                  checked={formData.sunExposure.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('sunExposure', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`sunExposure-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <h5 className='border-bottom pb-2 mb-3'>Opis</h5>
                        <div className='form-group mb-3'>
                          <label htmlFor='description' className='font-weight-bold'>Opis rośliny <span className='text-danger'>*</span></label>
                          <textarea
                            className='form-control'
                            id='description'
                            name='description'
                            rows={5}
                            value={formData.description}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sekcja 4: Dodatkowe cechy */}
                <div className='card mb-4 shadow-sm'>
                  <div className='card-header bg-light py-3'>
                    <h4 className='mb-0 font-weight-bold text-primary'>Dodatkowe cechy</h4>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='form-group'>
                          <label>Forma rośliny</label>
                          <div className='checkbox-group row'>
                            {formOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`form-${option.id}`}
                                  checked={formData.form.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('form', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`form-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className='form-group'>
                          <label htmlFor='growthRate'>Siła wzrostu</label>
                          <select
                            className='form-control'
                            id='growthRate'
                            name='growthRate'
                            value={formData.growthRate}
                            onChange={handleChange}
                          >
                            <option value=''>Wybierz siłę wzrostu</option>
                            {growthRateOptions.map(option => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className='form-group'>
                          <label>Pokrój</label>
                          <div className='checkbox-group row'>
                            {habitOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`habit-${option.id}`}
                                  checked={formData.habit.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('habit', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`habit-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className='form-group'>
                          <label htmlFor='leafType'>Typ liści</label>
                          <select
                            className='form-control'
                            id='leafType'
                            name='leafType'
                            value={formData.leafType}
                            onChange={handleChange}
                          >
                            <option value=''>Wybierz opcję</option>
                            {leafTypeOptions.map(option => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='form-group'>
                          <label>Walory</label>
                          <div className='checkbox-group row'>
                            {featuresOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`features-${option.id}`}
                                  checked={formData.features.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('features', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`features-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className='form-group'>
                          <label>Rodzaje ogrodów</label>
                          <div className='checkbox-group row'>
                            {gardenTypesOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`gardenTypes-${option.id}`}
                                  checked={formData.gardenTypes.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('gardenTypes', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`gardenTypes-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sekcja 5: Kwiaty i owoce */}
                <div className='card mb-4 shadow-sm'>
                  <div className='card-header bg-light py-3'>
                    <h4 className='mb-0 font-weight-bold text-primary'>Kwiaty i owoce</h4>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='form-group mb-3'>
                          <label className='font-weight-bold'>Pora kwitnienia</label>
                          <div className='checkbox-group row'>
                            {floweringSeasonOptions.map(option => (
                              <div key={option.id} className='form-check col-md-4'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`floweringSeason-${option.id}`}
                                  checked={formData.floweringSeason.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('floweringSeason', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`floweringSeason-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='form-group mb-3'>
                          <label className='font-weight-bold'>Owocowanie</label>
                          <div className='checkbox-group row'>
                            {fruitsOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`fruits-${option.id}`}
                                  checked={formData.fruits.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('fruits', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`fruits-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className='form-group mb-3'>
                          <label className='font-weight-bold'>Pora owocowania</label>
                          <div className='checkbox-group row'>
                            {fruitingSeasonOptions.map(option => (
                              <div key={option.id} className='form-check col-md-4'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`fruitingSeason-${option.id}`}
                                  checked={formData.fruitingSeason.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('fruitingSeason', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`fruitingSeason-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sekcja 6: Wymagania glebowe i zastosowania */}
                <div className='card mb-4 shadow-sm'>
                  <div className='card-header bg-light py-3'>
                    <h4 className='mb-0 font-weight-bold text-primary'>Wymagania glebowe i zastosowania</h4>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='form-group'>
                          <label>Wilgotność gleby</label>
                          <div className='checkbox-group row'>
                            {soilMoistureOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`soilMoisture-${option.id}`}
                                  checked={formData.soilMoisture.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('soilMoisture', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`soilMoisture-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className='form-group'>
                          <label>pH gleby</label>
                          <div className='checkbox-group row'>
                            {soilPhOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`soilPh-${option.id}`}
                                  checked={formData.soilPh.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('soilPh', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`soilPh-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='form-group'>
                          <label>Zastosowanie</label>
                          <div className='checkbox-group row'>
                            {usageOptions.map(option => (
                              <div key={option.id} className='form-check col-md-6'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`usage-${option.id}`}
                                  checked={formData.usage.includes(option.id)}
                                  onChange={() => handleMultiSelectChange('usage', option.id)}
                                />
                                <label className='form-check-label' htmlFor={`usage-${option.id}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sekcja 7: Dostępność w szkółkach (przeniesiona na dół) */}
                <div className='card mb-4 shadow-sm'>
                  <div className='card-header bg-light py-3'>
                    <h4 className='mb-0 font-weight-bold text-primary'>Dostępność w szkółkach</h4>
                  </div>
                  <div className='card-body'>
                    {selectedNurseries.map((nursery, index) => (
                      <div key={index} className='nursery-form-row border p-3 mb-3 rounded bg-light'>
                        <div className='row'>
                          <div className='col-12 d-flex justify-content-between mb-2'>
                            <h5 className='form-subsection-title font-weight-bold'>Szkółka {index + 1}</h5>
                            {selectedNurseries.length > 1 && (
                              <button
                                type='button'
                                className='btn btn-sm btn-danger'
                                onClick={() => removeNursery(index)}
                              >
                                <i className='fas fa-trash mr-1' /> Usuń szkółkę
                              </button>
                            )}
                          </div>

                          <div className='col-md-6'>
                            <div className='form-group mb-3'>
                              <label className='font-weight-bold'>Wybierz szkółkę</label>
                              {nurseryLoading
                                ? (
                                  <div className='d-flex align-items-center mt-2'>
                                    <span className='spinner-border spinner-border-sm mr-2' role='status' aria-hidden='true' />
                                    <span>Ładowanie szkółek...</span>
                                  </div>
                                  )
                                : availableNurseries.length === 0
                                  ? (
                                    <div className='alert alert-warning mt-2'>
                                      Nie znaleziono żadnych szkółek. <button className='btn btn-link p-0' onClick={() => { window.location.reload() }}>Odśwież stronę</button>
                                    </div>
                                    )
                                  : (
                                    <select
                                      className='form-control'
                                      required
                                      value={nursery.nurseryId || ''}
                                      onChange={(e) => handleNurseryChange(index, 'nurseryId', e.target.value)}
                                    >
                                      <option value=''>Wybierz szkółkę</option>
                                      {Array.isArray(availableNurseries) && availableNurseries.map(n => (
                                        <option key={n.id} value={n.id}>
                                          {n.name} ({n.location})
                                        </option>
                                      ))}
                                    </select>
                                    )}
                            </div>
                          </div>

                          <div className='col-md-3'>
                            <div className='form-group mb-3'>
                              <label className='font-weight-bold'>Dostępność</label>
                              <select
                                className='form-control'
                                value={nursery.availability}
                                onChange={(e) => handleNurseryChange(index, 'availability', e.target.value)}
                              >
                                <option value=''>Wybierz...</option>
                                <option value='dostępny'>Dostępny</option>
                                <option value='niedostępny'>Niedostępny</option>
                                <option value='na zamówienie'>Na zamówienie</option>
                              </select>
                            </div>
                          </div>

                          {/* <div className='col-md-3'>
                            <div className='form-group mb-3'>
                              <label className='font-weight-bold'>Cena</label>
                              <input
                                type='text'
                                className='form-control'
                                value={nursery.price || ''}
                                onChange={(e) => handleNurseryChange(index, 'price', e.target.value)}
                                placeholder='np. 45 zł'
                              />
                            </div>
                          </div> */}
                        </div>
                      </div>
                    ))}

                    <div className='mt-3 d-flex gap-2'>
                      <button
                        type='button'
                        className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                        onClick={addNursery}
                      >
                        <i className='fas fa-plus mr-2' /> Dodaj szkółkę
                      </button>

                      <Link to='/nurseries/new' className='btn btn-outline-success'>
                        <i className='fas fa-cog mr-2' /> Zarządzaj szkółkami
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Przyciski akcji */}
                <div className='form-actions mt-4 text-end'>
                  <button
                    type='button'
                    className={`
                        tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                        tw-text-white
                        tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                      `}
                    onClick={() => navigate('/plants')}
                  >
                    <i className='fas fa-times mr-2' /> Anuluj
                  </button>

                  <button
                    type='submit'
                    className={`
                        md:tw-ml-3 tw-w-full md:tw-w-fit tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-text-white
                        tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-dark-teal hover:tw-bg-opacity-90 focus:tw-bg-dark-teal
                      `}
                    disabled={loading}
                  >
                    {loading
                      ? (
                        <>
                          <span
                            className='tw-inline-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-current tw-border-r-transparent tw-mr-2'
                            role='status'
                            aria-hidden='true'
                          />
                          {isEdit ? 'Aktualizowanie...' : 'Zapisywanie...'}
                        </>
                        )
                      : (
                        <>
                          <i className='fas fa-save mr-2' />
                          {isEdit ? 'Zaktualizuj roślinę' : 'Dodaj roślinę'}
                        </>
                        )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default PlantForm
